// src/pages/FAQPage.jsx
import { useEffect } from 'react';
import { Link } from 'react-router-dom';

const DISPLAY = "'Playfair Display', Georgia, serif";
const SANS    = "'DM Sans', 'DM Sans', system-ui, sans-serif";
const MONO    = "'IBM Plex Mono', 'IBM Plex Mono', 'Courier New', monospace";
const BG0     = "#060a0f";
const GREEN   = "#3ddc84";
const MUTED   = "#888";
const BDR     = "#1a2a1f";

const NAV_LINKS = [
  { label: 'About', href: '/about' },
  { label: 'Pricing', href: '/pricing' },
  { label: 'FAQ', href: '/faq' },
];

function TopNav() {
  return (
    <nav style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100, borderBottom: `1px solid ${BDR}`, background: 'rgba(6,10,15,0.95)', backdropFilter: 'blur(12px)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 40px', height: 56 }}>
      <Link to="/" style={{ fontFamily: MONO, fontSize: '0.875rem', fontWeight: 600, color: GREEN, textDecoration: 'none', letterSpacing: '0.05em' }}>NUVPORT OS</Link>
      <div style={{ display: 'flex', gap: 32, alignItems: 'center' }}>
        {NAV_LINKS.map(l => (
          <Link key={l.href} to={l.href} style={{ fontFamily: SANS, fontSize: '0.875rem', fontWeight: 500, color: '#888', textDecoration: 'none' }}>{l.label}</Link>
        ))}
        <Link to="/" style={{ fontFamily: SANS, fontSize: '0.875rem', fontWeight: 600, background: GREEN, padding: '8px 20px', textDecoration: 'none', color: '#060a0f' }}>Start Free</Link>
      </div>
    </nav>
  );
}

function PageFooter() {
  return (
    <footer style={{ borderTop: `1px solid ${BDR}`, padding: '60px 40px', background: BG0 }}>
      <div style={{ maxWidth: 1200, margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 40 }}>
        <div>
          <p style={{ fontFamily: MONO, fontSize: '0.875rem', fontWeight: 600, color: GREEN, marginBottom: 8, letterSpacing: '0.05em' }}>NUVPORT OS</p>
          <p style={{ fontFamily: SANS, fontSize: '0.8125rem', color: '#555', maxWidth: 240, lineHeight: 1.6 }}>The financial intelligence environment.</p>
        </div>
        <div style={{ display: 'flex', gap: 60 }}>
          <div>
            <p style={{ fontFamily: SANS, fontSize: '0.7rem', fontWeight: 600, letterSpacing: '0.15em', color: '#444', marginBottom: 16, textTransform: 'uppercase' }}>Product</p>
            {[['About', '/about'], ['Pricing', '/pricing'], ['FAQ', '/faq']].map(([label, href]) => (
              <Link key={href} to={href} style={{ display: 'block', fontFamily: SANS, fontSize: '0.875rem', color: '#888', textDecoration: 'none', marginBottom: 10 }}>{label}</Link>
            ))}
          </div>
        </div>
      </div>
      <div style={{ maxWidth: 1200, margin: '40px auto 0', paddingTop: 32, borderTop: `1px solid ${BDR}`, display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16 }}>
        <p style={{ fontFamily: SANS, fontSize: '0.75rem', color: '#333' }}>© {new Date().getFullYear()} Nuvport OS. All rights reserved.</p>
        <p style={{ fontFamily: SANS, fontSize: '0.75rem', color: '#333' }}>Zagreb, Croatia · nuvportos.com</p>
      </div>
    </footer>
  );
}

const FAQS = [
  {
    q: "What is Nuvport OS?",
    a: "Nuvport OS is a financial intelligence platform built for serious retail investors. It gives you institutional-grade analysis — Risk Score, portfolio analytics, geopolitical intelligence, backtesting, and 21 integrated modules — at $29/month instead of the $24,000/year institutions pay for Bloomberg.",
  },
  {
    q: "What is the Risk Score?",
    a: "Your Risk Score is a number from 0 to 100 that represents how exposed your portfolio is right now. It is calculated from five components: geo-political exposure (30%), sector concentration (25%), asset volatility (20%), currency exposure (15%), and portfolio balance (10%). It updates every time your portfolio changes or the market closes.",
  },
  {
    q: "What is the difference between Free and Pro?",
    a: "Free gives you the Dashboard, Portfolio Builder, MarketWatch, and your Risk Score number. Pro unlocks all 21 modules and removes the 5-holding limit. The free tier is designed to show you the signal. Pro is where you understand it and act on it.",
  },
  {
    q: "Is my portfolio data safe?",
    a: "Yes. Your data is stored securely with row-level security — meaning your data is only ever accessible to you. We never sell your data. We never share it with third parties. Your portfolio information is yours alone.",
  },
  {
    q: "Can I cancel anytime?",
    a: "Yes. No contracts, no cancellation fees. You can cancel your Pro subscription at any time from your account settings. You will keep access until the end of your billing period.",
  },
  {
    q: "What assets does Nuvport support?",
    a: "Nuvport supports stocks, ETFs, cryptocurrencies, and commodities. You can track a mixed portfolio across all asset classes in one place.",
  },
  {
    q: "How do I add my portfolio?",
    a: "You can add holdings manually using the autocomplete search — type a ticker or asset name and select it. You can also import a CSV file if you want to add multiple holdings at once.",
  },
  {
    q: "Is this suitable for beginners?",
    a: "Nuvport is built for the self-directed investor who is serious about learning — not complete beginners, but not Wall Street professionals either. If you have started investing and want to understand what your portfolio is actually exposed to, Nuvport is built for you.",
  },
  {
    q: "How is Nuvport different from Robinhood or Trading 212?",
    a: "Robinhood and Trading 212 are brokerage apps — they let you buy and sell. Nuvport is an intelligence platform — it helps you understand what you own and what risks you are carrying. You use Nuvport alongside your brokerage, not instead of it.",
  },
  {
    q: "How much does it cost?",
    a: "Free tier is $0 forever — no credit card required. Pro is $29/month. Cancel anytime.",
  },
  {
    q: "Who built this?",
    a: "Nuvport OS was built by a 19-year-old global business student in Zagreb, Croatia. Read the full story on our About page.",
  },
];

export default function FAQPage() {
  useEffect(() => {
    document.title = 'FAQ — Nuvport OS';
    window.scrollTo(0, 0);
  }, []);

  return (
    <div style={{ background: BG0, minHeight: '100vh', color: '#ffffff' }}>
      <TopNav />

      {/* Hero */}
      <section style={{ padding: '140px 40px 80px', maxWidth: 720, margin: '0 auto' }}>
        <p style={{ fontFamily: SANS, fontSize: '0.7rem', fontWeight: 600, letterSpacing: '0.15em', color: MUTED, marginBottom: 24, textTransform: 'uppercase' }}>FAQ</p>
        <h1 style={{ fontFamily: DISPLAY, fontSize: 'clamp(2.5rem, 5vw, 3.5rem)', fontWeight: 800, lineHeight: 1.1, marginBottom: 16, margin: '0 0 16px' }}>
          Questions, answered honestly.
        </h1>
        <p style={{ fontFamily: SANS, fontSize: '1rem', color: MUTED, lineHeight: 1.7, marginTop: 16 }}>
          If something is not here, email us. We will answer.
        </p>
      </section>

      {/* FAQ list */}
      <section style={{ maxWidth: 720, margin: '0 auto', padding: '0 40px 160px' }}>
        {FAQS.map((faq, i) => (
          <div key={i} style={{ borderTop: `1px solid ${BDR}`, padding: '40px 0' }}>
            <p style={{ fontFamily: DISPLAY, fontSize: '1.125rem', fontWeight: 600, color: '#ffffff', marginBottom: 16, lineHeight: 1.4 }}>
              {faq.q}
            </p>
            <p style={{ fontFamily: SANS, fontSize: '0.9375rem', color: MUTED, lineHeight: 1.75 }}>
              {faq.a}
            </p>
          </div>
        ))}

        <div style={{ borderTop: `1px solid ${BDR}`, paddingTop: 40 }}>
          <p style={{ fontFamily: DISPLAY, fontSize: '1.125rem', fontWeight: 600, color: '#ffffff', marginBottom: 16 }}>
            Still have a question?
          </p>
          <p style={{ fontFamily: SANS, fontSize: '0.9375rem', color: MUTED, lineHeight: 1.75 }}>
            We are a small team and we read every message. Reach out at{' '}
            <a href="mailto:hello@nuvportos.com" style={{ color: '#ffffff', textDecoration: 'none', borderBottom: '1px solid #333' }}>
              hello@nuvportos.com
            </a>
          </p>
        </div>
      </section>

      <PageFooter />
    </div>
  );
}
